import { Alert, Snackbar } from "@mui/material"
import React, { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { pagesUrl } from "@app/core/appConstants"
import { getErrorMessage } from "@app/core/error/error-handler"
import { Children } from "@app/core/type/Children"

export const ErrorContext = React.createContext<(err: Error) => void>(() => {
  // do nothing, function will be set later
})

export default function ErrorSnackbar({ children }: Readonly<Children>): React.JSX.Element {
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState<boolean>(false)

  const navigate = useNavigate()

  function handleClose(): void {
    setErrorMsg(undefined)
    setOpen(false)
  }

  const openErrorSnackbar: (err: Error) => void = useCallback(
    (err: Error) => {
      if (err) {
        const errorMessage = getErrorMessage(err)
        setErrorMsg(errorMessage)
      } else {
        setErrorMsg("Une erreur s'est produite")
      }

      if (err.message === "FORBIDDEN_ORGANIZATION_USER" || err.message === "PROJECT_USER_NOT_FOUND") {
        // Every request about a project the user cannot open comes back with one of these codes. Replacing the entry
        // leaves a single projects page in the history, and going back does not return to the refused project.
        navigate(pagesUrl.PROJECTS_PAGE, { replace: true })
      }

      setOpen(true)
    },
    [navigate]
  )

  return (
    <ErrorContext.Provider value={openErrorSnackbar}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%", textAlign: "center", whiteSpace: "pre-line" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </ErrorContext.Provider>
  )
}
